/**
 * MyCertificatesGallery
 * ─────────────────────
 * Lets a certified (or PRO+) user upload up to 5 ADDITIONAL certificate photos
 * (e.g. EUIPO trademark, ISO 9001, manufacturer welding diplomas, attendance
 * certificates) that appear in their public profile and Hall-of-Fame card
 * alongside their primary SauberEko® certificate.
 *
 * Fully i18n — 5 languages (sk / en / cs / de / pl).
 */
import React, { useEffect, useRef, useState } from "react";
import { Award, Plus, Trash2, X, Loader2, AlertCircle, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const T = {
  sk: {
    title: "Moje ďalšie certifikáty",
    sub: "Pridaj fotky svojich ďalších certifikátov (EUIPO, ISO, výrobcovia, kurzy…). Zobrazia sa na tvojom verejnom profile a v Sieni slávy.",
    addBtn: "Pridať fotku",
    empty: "Zatiaľ nemáš pridané žiadne ďalšie certifikáty.",
    captionPlaceholder: "Popis (napr. EUIPO ochranná známka č. 019269791)",
    save: "Uložiť",
    cancel: "Zrušiť",
    deleting: "Odstránenie…",
    confirmDelete: "Naozaj odstrániť túto fotku certifikátu?",
    maxReached: (n) => `Maximum ${n} fotiek dosiahnuté — najprv odstráň niektorú.`,
    sizeError: "Fotka je príliš veľká (max 5 MB).",
    proRequired: "Pridávanie ďalších certifikátov je dostupné pre PRO/GOLD/EXCELLENT používateľov a SauberEko® certifikovaných.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Nahrávam…",
    preview: "Náhľad",
    close: "Zavrieť",
  },
  en: {
    title: "My other certificates",
    sub: "Add photos of your additional certificates (EUIPO, ISO, manufacturer, courses…). They appear on your public profile and Hall of Fame card.",
    addBtn: "Add photo",
    empty: "You haven't added any other certificates yet.",
    captionPlaceholder: "Caption (e.g. \"EUIPO trademark No. 019269791\")",
    save: "Save",
    cancel: "Cancel",
    deleting: "Deleting…",
    confirmDelete: "Really remove this certificate photo?",
    maxReached: (n) => `Maximum ${n} photos reached — remove one first.`,
    sizeError: "Photo too large (max 5 MB).",
    proRequired: "Adding extra certificates is available for PRO/GOLD/EXCELLENT members and SauberEko® certified.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Uploading…",
    preview: "Preview",
    close: "Close",
  },
  cs: {
    title: "Moje další certifikáty",
    sub: "Přidej fotky dalších certifikátů (EUIPO, ISO, výrobci, kurzy…). Zobrazí se na tvém veřejném profilu a v Síni slávy.",
    addBtn: "Přidat fotku",
    empty: "Zatím nemáš přidané žádné další certifikáty.",
    captionPlaceholder: "Popis (např. EUIPO ochranná známka č. 019269791)",
    save: "Uložit",
    cancel: "Zrušit",
    deleting: "Odstraňování…",
    confirmDelete: "Opravdu odstranit tuto fotku certifikátu?",
    maxReached: (n) => `Maximum ${n} fotek dosaženo — nejdříve odstraň některou.`,
    sizeError: "Fotka je příliš velká (max 5 MB).",
    proRequired: "Přidávání dalších certifikátů je dostupné pro PRO/GOLD/EXCELLENT uživatele a SauberEko® certifikované.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Nahrávám…",
    preview: "Náhled",
    close: "Zavřít",
  },
  de: {
    title: "Meine weiteren Zertifikate",
    sub: "Füge Fotos zusätzlicher Zertifikate hinzu (EUIPO, ISO, Hersteller, Kurse…). Sie erscheinen in deinem öffentlichen Profil und in der Hall of Fame.",
    addBtn: "Foto hinzufügen",
    empty: "Du hast noch keine weiteren Zertifikate hinzugefügt.",
    captionPlaceholder: "Beschreibung (z. B. EUIPO-Marke Nr. 019269791)",
    save: "Speichern",
    cancel: "Abbrechen",
    deleting: "Entferne…",
    confirmDelete: "Dieses Zertifikatsfoto wirklich entfernen?",
    maxReached: (n) => `Maximum von ${n} Fotos erreicht — entferne zuerst eines.`,
    sizeError: "Foto zu groß (max 5 MB).",
    proRequired: "Das Hinzufügen weiterer Zertifikate ist für PRO/GOLD/EXCELLENT-Mitglieder und SauberEko®-Zertifizierte verfügbar.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Lade hoch…",
    preview: "Vorschau",
    close: "Schließen",
  },
  pl: {
    title: "Moje pozostałe certyfikaty",
    sub: "Dodaj zdjęcia dodatkowych certyfikatów (EUIPO, ISO, producenci, kursy…). Pojawią się na Twoim publicznym profilu i w Galerii sław.",
    addBtn: "Dodaj zdjęcie",
    empty: "Nie dodano jeszcze żadnych dodatkowych certyfikatów.",
    captionPlaceholder: "Opis (np. Znak towarowy EUIPO nr 019269791)",
    save: "Zapisz",
    cancel: "Anuluj",
    deleting: "Usuwanie…",
    confirmDelete: "Na pewno usunąć to zdjęcie certyfikatu?",
    maxReached: (n) => `Osiągnięto maksimum ${n} zdjęć — najpierw usuń któreś.`,
    sizeError: "Zdjęcie zbyt duże (max 5 MB).",
    proRequired: "Dodawanie dodatkowych certyfikatów dostępne dla członków PRO/GOLD/EXCELLENT i certyfikowanych przez SauberEko®.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Wysyłanie…",
    preview: "Podgląd",
    close: "Zamknij",
  },
  fr: {
    title: "Mes autres certificats",
    sub: "Ajoute des photos de tes autres certificats (EUIPO, ISO, fabricants, formations…). Ils apparaîtront sur ton profil public et dans le Hall of Fame.",
    addBtn: "Ajouter une photo",
    empty: "Tu n'as encore ajouté aucun autre certificat.",
    captionPlaceholder: "Description (ex. : marque EUIPO n° 019269791)",
    save: "Enregistrer",
    cancel: "Annuler",
    deleting: "Suppression…",
    confirmDelete: "Vraiment supprimer cette photo de certificat ?",
    maxReached: (n) => `Maximum de ${n} photos atteint — supprime-en une d'abord.`,
    sizeError: "Photo trop volumineuse (max 5 Mo).",
    proRequired: "L'ajout d'autres certificats est réservé aux utilisateurs PRO/GOLD/EXCELLENT et certifiés SauberEko®.",
    counter: (n, max) => `${n} / ${max}`,
    uploading: "Envoi…",
    preview: "Aperçu",
    close: "Fermer",
  },
};

const MAX_EXTRAS = 5;
const ALLOWED_TIERS = ["pro", "gold", "excellent"];

export default function MyCertificatesGallery({ user }) {
  const { lang } = useLang();
  const t = T[lang] || T.sk;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingCaption, setPendingCaption] = useState("");
  const fileRef = useRef(null);

  const canAdd = user?.certified || ALLOWED_TIERS.includes((user?.plan_tier || "").toLowerCase());

  useEffect(() => {
    if (!user) return;
    api.get("/me/certificate-photos/extra").then((r) => {
      setItems(r.data.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr(t.sizeError);
      return;
    }
    setErr("");
    const reader = new FileReader();
    reader.onload = () => setPendingFile(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!pendingFile) return;
    if (items.length >= MAX_EXTRAS) {
      setErr(t.maxReached(MAX_EXTRAS));
      return;
    }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/me/certificate-photos/extra", {
        photo_base64: pendingFile,
        caption: pendingCaption.trim() || null,
      });
      setItems((cur) => [...cur, r.data.item]);
      setPendingFile(null);
      setPendingCaption("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setErr(e.response?.data?.detail || "upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    setBusy(true);
    try {
      await api.delete(`/me/certificate-photos/extra/${id}`);
      setItems((cur) => cur.filter((c) => c.id !== id));
    } finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <section className="border border-zinc-800 bg-[var(--wb-surface)] p-5 sm:p-6 mt-6" data-testid="my-certs-gallery">
      <div className="flex items-center gap-2 mb-2">
        <Award className="w-4 h-4 text-gold-500" />
        <h2 className="font-display font-bold text-lg text-white">{t.title}</h2>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500" data-testid="my-certs-counter">
          {t.counter(items.length, MAX_EXTRAS)}
        </span>
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed mb-4">{t.sub}</p>

      {!canAdd && (
        <div className="border border-gold-700/30 bg-gold-950/10 p-3 mb-4 inline-flex items-start gap-2 text-xs text-gold-300/80">
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{t.proRequired}</span>
        </div>
      )}

      {/* Gallery */}
      {items.length === 0 ? (
        <p className="text-xs text-zinc-600 font-mono mb-4">{t.empty}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {items.map((it) => (
            <div key={it.id} className="group relative border border-zinc-800 hover:border-gold-700/50 transition-colors">
              <button
                onClick={() => setPreviewPhoto(it)}
                className="block w-full aspect-square bg-black overflow-hidden"
                data-testid={`my-cert-thumb-${it.id}`}
              >
                <img src={it.photo} alt={it.caption || "Certificate"} className="w-full h-full object-cover" />
              </button>
              {it.caption && (
                <div className="px-2 py-1.5 text-[10px] font-mono text-zinc-400 line-clamp-2">{it.caption}</div>
              )}
              <button
                onClick={() => remove(it.id)}
                disabled={busy}
                data-testid={`my-cert-del-${it.id}`}
                className="absolute top-1 right-1 w-7 h-7 bg-red-950/80 border border-red-800 text-red-300 hover:bg-red-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title={t.deleting}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new */}
      {canAdd && items.length < MAX_EXTRAS && (
        <div className="border border-dashed border-zinc-800 p-3">
          {!pendingFile ? (
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-gold-700/40 text-gold-300 text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-gold-950/30 transition-colors" data-testid="my-cert-add">
              <Plus className="w-4 h-4" /> {t.addBtn}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-3 items-start">
                <img src={pendingFile} alt="preview" className="w-20 h-20 object-cover border border-zinc-700" />
                <div className="flex-1">
                  <input
                    type="text"
                    value={pendingCaption}
                    onChange={(e) => setPendingCaption(e.target.value)}
                    placeholder={t.captionPlaceholder}
                    maxLength={120}
                    className="wb-input w-full px-3 py-2 text-sm"
                    data-testid="my-cert-caption"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="my-cert-save"
                  className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {busy ? t.uploading : t.save}
                </button>
                <button
                  onClick={() => { setPendingFile(null); setPendingCaption(""); if (fileRef.current) fileRef.current.value = ""; }}
                  disabled={busy}
                  className="wb-btn-ghost px-4 py-2 text-xs"
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm flex items-center gap-2" data-testid="my-cert-error">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Lightbox preview */}
      {previewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)} data-testid="my-cert-lightbox">
          <button
            onClick={() => setPreviewPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 border border-zinc-700 hover:border-gold-500 text-zinc-300 hover:text-white flex items-center justify-center"
            aria-label={t.close}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={previewPhoto.photo} alt={previewPhoto.caption || t.preview} className="max-w-full max-h-[85vh] object-contain border border-gold-700/40 mx-auto" />
            {previewPhoto.caption && (
              <p className="text-center text-zinc-300 mt-3 font-mono text-sm">{previewPhoto.caption}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
